import React, { useEffect, useState } from 'react';
import { Plus, Shield, Building2, Users as UsersIcon, Trash2, Ban, CheckCircle, Edit2, X, Loader2, RefreshCw, MapPin } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi, teamsApi, rolesApi, UserRecord, TeamRecord, RoleRecord } from '../services/creatorApi';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
export function SuperAdminSettings() {
  const {
    currentUser,
    workspaces,
    roles,
    refreshTeams
  } = useWorkspace();
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'workspaces'>('users');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [userToDelete, setUserToDelete] = useState<UserRecord | null>(null);
  // Create Team Modal state
  const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamRegions, setNewTeamRegions] = useState<string[]>([]);
  const [newRegionInput, setNewRegionInput] = useState('');
  const [newTeamLogoUrl, setNewTeamLogoUrl] = useState('');
  const [newTeamPrimaryColor, setNewTeamPrimaryColor] = useState('#2563eb');
  // Security check
  if (currentUser.Role !== 'Super Admin') {
    return <div className="p-8 text-center text-red-600" data-id="element-6620" data-mp="3qpoty9js3vr3">
        Access Denied: Super Admin privileges required.
      </div>;
  }
  const loadUsers = async () => {
    setLoading(true);
    try {
      const users = await usersApi.list();
      setAllUsers(users);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadUsers();
  }, []);
  const filteredUsers = selectedTeamFilter === 'all' ? allUsers : allUsers.filter(u => u.Team === selectedTeamFilter);
  const handleToggleStatus = async (user: UserRecord) => {
    const newStatus = user.Status === 'Active' ? 'Suspended' : 'Active';
    try {
      await usersApi.update(user.ID, {
        Status: newStatus
      });
      toast.success(`User ${newStatus === 'Active' ? 'activated' : 'suspended'}`);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };
  const handleDeleteUser = (user: UserRecord) => {
    setUserToDelete(user);
  };
  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await usersApi.delete(userToDelete.ID);
      toast.success('User deleted');
      loadUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
    setUserToDelete(null);
  };
  const handleStartEditRole = (userId: string, currentRole: string) => {
    setEditingUserId(userId);
    setSelectedRole(currentRole);
  };
  const handleSaveRole = async (userId: string) => {
    try {
      await usersApi.update(userId, {
        Role: selectedRole as any
      });
      toast.success('Role updated');
      setEditingUserId(null);
      loadUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };
  const handleAddRegion = (e: React.FormEvent) => {
    e.preventDefault();
    if (newRegionInput.trim() && !newTeamRegions.includes(newRegionInput.trim())) {
      setNewTeamRegions([...newTeamRegions, newRegionInput.trim()]);
      setNewRegionInput('');
    }
  };
  const handleRemoveRegion = (region: string) => {
    setNewTeamRegions(newTeamRegions.filter(r => r !== region));
  };
  const resetCreateTeamForm = () => {
    setNewTeamName('');
    setNewTeamRegions([]);
    setNewRegionInput('');
    setNewTeamLogoUrl('');
    setNewTeamPrimaryColor('#2563eb');
  };
  const handleCreateTeam = () => {
    setShowCreateTeamModal(true);
  };
  const handleSubmitCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) {
      toast.error('Team name is required');
      return;
    }
    setIsCreatingTeam(true);
    try {
      const regionRows = newTeamRegions.map(r => ({
        Region_Name: r
      }));
      await teamsApi.create({
        Team_Name: newTeamName.trim(),
        Team_ID: teamsApi.generateTeamId(newTeamName.trim()),
        Logo_Url: newTeamLogoUrl.trim() || undefined,
        Primary_Color: newTeamPrimaryColor || undefined,
        ...(regionRows.length > 0 ? {
          Team_Regions: regionRows
        } : {})
      });
      toast.success('Team created successfully');
      setShowCreateTeamModal(false);
      resetCreateTeamForm();
      refreshTeams();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to create team', {
        description: msg
      });
    } finally {
      setIsCreatingTeam(false);
    }
  };
  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case 'Super Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Team Manager':
        return 'bg-blue-100 text-blue-800';
      case 'Operator':
        return 'bg-green-100 text-green-800';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const activeUsers = allUsers.filter(u => u.Status === 'Active');
  return <div className="space-y-6" data-id="element-6621" data-mp="35z9ylgxj7dfb">
      <div className="flex items-center justify-between" data-id="element-6622" data-mp="35yi3e77ujhpk">
        <div data-id="element-6623" data-mp="31zho2yokux7b">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-6624" data-mp="31xy5h1fg8a2d">
            Super Admin Settings
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-6625" data-mp="1umpki08otybi">
            Global management of users and teams across all workspaces.
          </p>
        </div>
        <button onClick={loadUsers} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-6626" data-mp="1s06qst82qt7c">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-6627" data-mp="1i3214jf0sfls" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4" data-id="element-6628" data-mp="1n0lny7psa0pc">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6629" data-mp="1n3g8s0cplnja">
          <div className="flex items-center gap-3" data-id="element-6630" data-mp="198xfceeqtud4">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-6631" data-mp="1e1gvnj4tf3lm">
              <UsersIcon className="h-5 w-5 text-blue-600" data-id="element-6632" data-mp="1e31pt7n7wkiy" />
            </div>
            <div data-id="element-6633" data-mp="zdm1rzf2mt6y">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6634" data-mp="z9y6ovtz9yq4">
                {allUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6635" data-mp="qbcox2wbynqe">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6636" data-mp="ucpwpiizh0zy">
          <div className="flex items-center gap-3" data-id="element-6637" data-mp="uesljz8ego33">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-6638" data-mp="bpp2tc6kh94w">
              <Building2 className="h-5 w-5 text-green-600" data-id="element-6639" data-mp="6pknvqlb4mgs" />
            </div>
            <div data-id="element-6640" data-mp="6rno13g6bvz8">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6641" data-mp="kpc9ace5l54t">
                {workspaces.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6642" data-mp="fpr7lu7lmioe">Teams</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6643" data-mp="umqchab2fxf6">
          <div className="flex items-center gap-3" data-id="element-6644" data-mp="pogfcu5s6mqx">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-6645" data-mp="pnx9ibtenot9">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-6646" data-mp="13gd3yo88qeik" />
            </div>
            <div data-id="element-6647" data-mp="13hob8y0wa4ef">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6648" data-mp="zei13fanvt2o">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6649" data-mp="1dd841bk5wmyy">Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-6650" data-mp="18fzid0zt20sy">
          <div className="flex items-center gap-3" data-id="element-6651" data-mp="2frni8nyrhizs">
            <div className="p-2 bg-yellow-50 rounded-lg" data-id="element-6652" data-mp="2fr41hakul0nc">
              <UsersIcon className="h-5 w-5 text-yellow-600" data-id="element-6653" data-mp="2js7hpy6t4yp3" />
            </div>
            <div data-id="element-6654" data-mp="2osbo2ve04d1l">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-6655" data-mp="2orsfw6e5l30r">
                {activeUsers.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-6656" data-mp="2tpu0wwv53cev">Active Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-6657" data-mp="1w1jxqejmaim6">
        <nav className="-mb-px flex space-x-8" data-id="element-6658" data-mp="21398dt2xgu9q">
          <button onClick={() => setActiveTab('users')} className={`${activeTab === 'users' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-6659" data-mp="261ki83t834ry">
            <UsersIcon className="h-4 w-4" data-id="element-6660" data-mp="1ibghq5bbzl56" />
            All Users ({allUsers.length})
          </button>
          <button onClick={() => setActiveTab('workspaces')} className={`${activeTab === 'workspaces' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`} data-id="element-6661" data-mp="1asqq9ssh6au5">
            <Building2 className="h-4 w-4" data-id="element-6662" data-mp="vuybx3ynzshj" />
            Teams ({workspaces.length})
          </button>
        </nav>
      </div>

      {activeTab === 'users' && <div className="space-y-4" data-id="element-6663" data-mp="n2mntor0wh4q">
          <div className="flex justify-between items-center" data-id="element-6664" data-mp="n1l9nbepneis">
            <div className="flex items-center gap-2" data-id="element-6665" data-mp="i4utyp6tikxw">
              <span className="text-sm text-gray-500" data-id="element-6666" data-mp="i1gov96rrxmr">Filter by Team:</span>
              <select className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500" value={selectedTeamFilter} onChange={e => setSelectedTeamFilter(e.target.value)} data-id="element-6667" data-mp="d6jiyb4se4e3">
                <option value="all" data-id="element-6668" data-mp="1iqh7rua54l74">All Teams ({allUsers.length} users)</option>
                {workspaces.map(ws => <option key={ws.id} value={ws.name} data-id="element-6669" data-mp="1drcxt1fe6jfq">
                    {ws.name} (
                    {allUsers.filter(u => u.Team === ws.name).length} users)
                  </option>)}
              </select>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6670" data-mp="zrv88ju9v25b">
            {loading ? <div className="p-12 text-center" data-id="element-6671" data-mp="uubv5n9b8vey">
                <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-6672" data-mp="uvmwlgfa9r2w" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-6673" data-mp="18ue9iziet6pg">Loading users...</p>
              </div> : filteredUsers.length === 0 ? <div className="p-12 text-center" data-id="element-6674" data-mp="2k7p2llcpyzka">
                <UsersIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-6675" data-mp="2k6f1soehfd9y" />
                <p className="mt-2 text-sm text-gray-500" data-id="element-6676" data-mp="2g5lffzgou6j6">No users found</p>
              </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-6677" data-mp="2nokvbv4xxfxs">
                <thead className="bg-gray-50" data-id="element-6678" data-mp="2spinn0jnzqd0">
                  <tr data-id="element-6679" data-mp="2sn5wbws0o3na">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6680" data-mp="2drzrve8kkcw2">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6681" data-mp="2irb8mn8xridn">
                      Team
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6682" data-mp="3bep045tv7lqd">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6683" data-mp="31pnjqejer2bv">
                      Regions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6684" data-mp="3pdexlmkljnf9">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6685" data-mp="7vj55tv5clei">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-6686" data-mp="3uzp7icuupzcc">
                  {filteredUsers.map(user => <tr key={user.ID} className="hover:bg-gray-50" data-id="element-6687" data-mp="3q4rwx1hxbhxc">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6688" data-mp="3l4x7l2dsr0ee">
                        <div className="flex items-center" data-id="element-6689" data-mp="3gcdt837ac18j">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-xs font-medium text-white mr-3" data-id="element-6690" data-mp="3gat3qrztv8hj">
                            {user.User_Name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                          <div data-id="element-6691" data-mp="32exl8n9cbzeo">
                            <div className="text-sm font-medium text-gray-900" data-id="element-6692" data-mp="2xd8az9txzprn">
                              {user.User_Name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-6693" data-mp="1cxtrpb3uuyh1">
                              {user.Email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6694" data-mp="1qv8j2ldo5gbn">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-6695" data-mp="1qte8o100x6lt">
                          {user.Team}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6696" data-mp="13663rk6ibzkb">
                        {editingUserId === user.ID ? <div className="flex items-center gap-2" data-id="element-6697" data-mp="17xo6ods3ca7x">
                            <select className="rounded-md border-gray-300 text-sm" value={selectedRole} onChange={e => setSelectedRole(e.target.value)} data-id="element-6698" data-mp="bf00iuvhplqt">
                              <option value="Super Admin" data-id="element-6699" data-mp="3sf08l5hzqhkt">Super Admin</option>
                              <option value="Team Manager" data-id="element-6700" data-mp="3njkwoj3jmvr1">Team Manager</option>
                              <option value="Operator" data-id="element-6701" data-mp="62l3y7dlivmj">Operator</option>
                              <option value="Viewer" data-id="element-6702" data-mp="1b31fevxcmzg">Viewer</option>
                            </select>
                            <button onClick={() => handleSaveRole(user.ID)} className="p-1 text-green-600 hover:text-green-800" data-id="element-6703" data-mp="f1oolfsevj35">
                              <CheckCircle className="h-4 w-4" data-id="element-6704" data-mp="ozcn8gt51aql" />
                            </button>
                            <button onClick={() => setEditingUserId(null)} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-6705" data-mp="k2cg02kjy1xw">
                              <X className="h-4 w-4" data-id="element-6706" data-mp="2lu8ot4fc4xoz" />
                            </button>
                          </div> : <div className="flex items-center gap-2" data-id="element-6707" data-mp="169ghpb35m2zi">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(user.Role)}`} data-id="element-6708" data-mp="16bsvdc1vpr6r">
                              {user.Role}
                            </span>
                            {user.ID !== currentUser.ID && <button onClick={() => handleStartEditRole(user.ID, user.Role)} className="p-1 text-gray-400 hover:text-blue-600" title="Change Role" data-id="element-6709" data-mp="mlzazj1hueck">
                                <Edit2 className="h-3 w-3" data-id="element-6710" data-mp="22y60r4qip1n2" />
                              </button>}
                          </div>}
                      </td>
                      <td className="px-6 py-4" data-id="element-6711" data-mp="21itkkiv4wl1u">
                        <div className="flex flex-wrap gap-1 max-w-[150px]" data-id="element-6712" data-mp="21i0m056bx1us">
                          {user.User_Regions && user.User_Regions.length > 0 ? user.User_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700" data-id="element-6713" data-mp="1rmqite472off" data-mpi={i}>
                                {r.Region_Name}
                              </span>) : <span className="text-xs text-gray-400 italic" data-id="element-6714" data-mp="2aiyr6qsi7fey">
                              All
                            </span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-6715" data-mp="3362riignj5ke">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${user.Status === 'Active' ? 'bg-green-100 text-green-800' : user.Status === 'Suspended' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-6716" data-mp="3bi4mx2qhvf8o">
                          {user.Status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6717" data-mp="3uf5tgrzp5neq">
                        {user.ID !== currentUser.ID && <div className="flex items-center justify-end gap-2" data-id="element-6718" data-mp="723b2pifrprp">
                            {user.Status === 'Active' ? <button onClick={() => handleToggleStatus(user)} className="text-yellow-600 hover:text-yellow-900" title="Suspend" data-id="element-6719" data-mp="c16bfwnh17tc">
                                <Ban className="h-4 w-4" data-id="element-6720" data-mp="179rhr3peyy4n" />
                              </button> : <button onClick={() => handleToggleStatus(user)} className="text-green-600 hover:text-green-900" title="Activate" data-id="element-6721" data-mp="12bg8ov6y0rnh">
                                <CheckCircle className="h-4 w-4" data-id="element-6722" data-mp="nnmqr3udxd1q" />
                              </button>}
                            <button onClick={() => handleDeleteUser(user)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-6723" data-mp="jj59pcfetdun">
                              <Trash2 className="h-4 w-4" data-id="element-6724" data-mp="4vadvn63utdc" />
                            </button>
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </div>}

      {activeTab === 'workspaces' && <div className="space-y-4" data-id="element-6725" data-mp="2gd5g8wd1un34">
          <div className="flex justify-end" data-id="element-6726" data-mp="22kpdzxrz2t39">
            <button onClick={handleCreateTeam} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-6727" data-mp="22htei50h7wcp">
              <Plus className="h-4 w-4" data-id="element-6728" data-mp="2v6qs3utqt4z4" />
              Create Team
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6729" data-mp="2lae1x6xep994">
            {workspaces.map(ws => <div key={ws.id} className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-6730" data-mp="2q8z256cnrsxk">
                <div className="flex items-start justify-between mb-4" data-id="element-6731" data-mp="19tnujkh8ybdx">
                  <div className="p-2 bg-blue-50 rounded-lg" data-id="element-6732" data-mp="1evd4sxwnal0y">
                    <Building2 className="h-6 w-6 text-blue-600" data-id="element-6733" data-mp="1p3pnjoi30uu9" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-6734" data-mp="1u2k4sm0eibc9">
                  {ws.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4" data-id="element-6735" data-mp="1g3swxhc4tlwk">ID: {ws.id}</p>
                <div className="mt-4 space-y-2" data-id="element-6736" data-mp="1k4cq69tl45df">
                  <div className="flex justify-between text-sm" data-id="element-6737" data-mp="1k31oqgnm39ph">
                    <span className="text-gray-500" data-id="element-6738" data-mp="16cy6s1vrm25u">Users</span>
                    <span className="font-medium text-gray-900" data-id="element-6739" data-mp="16bepc7ydhl2h">
                      {allUsers.filter(u => u.Team === ws.name).length}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-6740" data-mp="wcz06aiwolvm">
                    <span className="text-gray-500" data-id="element-6741" data-mp="11ewohj65y6vf">Regions</span>
                    <span className="font-medium text-gray-900" data-id="element-6742" data-mp="ndu02zrp4khc">
                      {ws.subRegions.length}
                    </span>
                  </div>
                  {ws.subRegions.length > 0 && <div className="flex flex-wrap gap-1 pt-1" data-id="element-6743" data-mp="8qvbrdo4y66u">
                      {ws.subRegions.map(r => <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700" data-id="element-6744" data-mp="3tvgzywqkpv1">
                          <MapPin className="h-3 w-3" data-id="element-6745" data-mp="dp6mdqm98kq4" />
                          {r}
                        </span>)}
                    </div>}
                </div>
              </div>)}
          </div>
        </div>}

      {/* Create Team Modal */}
      {showCreateTeamModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-6746" data-mp="wh94l6ejo92h">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-6747" data-mp="whr7umzmjnjw">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
          setShowCreateTeamModal(false);
          resetCreateTeamForm();
        }} data-id="element-6748" data-mp="1ai1zvtco786n" />

            <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:w-full sm:max-w-lg" data-id="element-6749" data-mp="32ook9jxct3l">
              <div className="px-4 py-5 sm:p-6" data-id="element-6750" data-mp="c4xuem48zyan">
                <div className="flex items-center justify-between mb-5" data-id="element-6751" data-mp="c338b4h8q04z">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" data-id="element-6752" data-mp="gwpkywnem40s">
                    Create New Team
                  </h3>
                  <button onClick={() => {
                setShowCreateTeamModal(false);
                resetCreateTeamForm();
              }} className="text-gray-400 hover:text-gray-500" data-id="element-6753" data-mp="lxdafdvzicsw">
                    <X className="h-6 w-6" data-id="element-6754" data-mp="zu8ppo2tx0he" />
                  </button>
                </div>

                <form onSubmit={handleSubmitCreateTeam} className="space-y-4" data-id="element-6755" data-mp="2xm4kv7ic7p2h">
                  <div data-id="element-6756" data-mp="31n6mtw3n2f85">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6757" data-mp="31mncb0gbit45">
                      Team Name <span className="text-red-500" data-id="element-6758" data-mp="2u473trk3appb">*</span>
                    </label>
                    <input type="text" required placeholder="e.g. Marketing, Global, North America" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTeamName} onChange={e => setNewTeamName(e.target.value)} autoFocus data-id="element-6759" data-mp="2paupqbd1e1at" />
                  </div>

                  <div data-id="element-6760" data-mp="33zqu9yfvww6o">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6761" data-mp="33yz2yztblkm7">
                      Regions (Optional)
                    </label>
                    <div className="mt-1 flex gap-2" data-id="element-6762" data-mp="1sm6d5uk3b68s">
                      <input type="text" placeholder="e.g. New York, London" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRegionInput} onChange={e => setNewRegionInput(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddRegion(e);
                    }
                  }} data-id="element-6763" data-mp="1sjk4tspdkmpi" />
                      <button type="button" onClick={handleAddRegion} disabled={!newRegionInput.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50" data-id="element-6764" data-mp="33ondcn4p1lj">
                        Add
                      </button>
                    </div>

                    {newTeamRegions.length > 0 && <div className="mt-3 space-y-2 max-h-32 overflow-y-auto" data-id="element-6765" data-mp="229lk0n31dely">
                        {newTeamRegions.map(region => <div key={region} className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-100" data-id="element-6766" data-mp="2g7sc1mlb0kdp">
                            <span className="text-sm font-medium text-gray-700" data-id="element-6767" data-mp="1imnto7bdo6ax">
                              {region}
                            </span>
                            <button type="button" onClick={() => handleRemoveRegion(region)} className="text-gray-400 hover:text-red-500" data-id="element-6768" data-mp="1xclfiglu4jwy">
                              <X className="h-4 w-4" data-id="element-6769" data-mp="zwoag127xlff" />
                            </button>
                          </div>)}
                      </div>}
                  </div>

                  <div data-id="element-6770" data-mp="34vxs241niyrl">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6771" data-mp="34v4i6u7ebt58">
                      Logo URL (Optional)
                    </label>
                    <input type="url" placeholder="https://example.com/logo.png" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTeamLogoUrl} onChange={e => setNewTeamLogoUrl(e.target.value)} data-id="element-6772" data-mp="3dpsmah18j4sg" />
                  </div>

                  <div data-id="element-6773" data-mp="2v0mazf2rtfgo">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-6774" data-mp="2uwoqoq4gihv7">
                      Primary Color (Optional)
                    </label>
                    <div className="mt-1 flex gap-2 items-center" data-id="element-6775" data-mp="a7sp7wx7wjtm">
                      <input type="color" className="h-8 w-8 rounded border border-gray-300 cursor-pointer p-0" value={newTeamPrimaryColor} onChange={e => setNewTeamPrimaryColor(e.target.value)} data-id="element-6776" data-mp="kcheu9biwq4e" />
                      <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm uppercase" value={newTeamPrimaryColor} onChange={e => setNewTeamPrimaryColor(e.target.value)} pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$" placeholder="#2563eb" data-id="element-6777" data-mp="6m5ke3rgufa3" />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-3" data-id="element-6778" data-mp="1m1bfxxfg0rka">
                    <button type="button" onClick={() => {
                  setShowCreateTeamModal(false);
                  resetCreateTeamForm();
                }} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" data-id="element-6779" data-mp="1i09f5c5vo7g7">
                      Cancel
                    </button>
                    <button type="submit" disabled={isCreatingTeam || !newTeamName.trim()} className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50" data-id="element-6780" data-mp="1hjqhs2v729hr">
                      {isCreatingTeam ? <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" data-id="element-6781" data-mp="37x7k9mdzvwwr" />
                          Creating...
                        </> : 'Create Team'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>}

      <ConfirmModal isOpen={!!userToDelete} title="Delete User" message={`Delete ${userToDelete?.User_Name}?`} confirmLabel="Delete" variant="danger" onCancel={() => setUserToDelete(null)} onConfirm={confirmDelete} data-id="element-6782" data-mp="3m26q049t4a04" />
    </div>;
}